// file 2 - SeeMoreTitle.js
import { t } from 'i18next';
import { Typography } from '../../../Typography/typography';
import styles from './EmployerPortalJobDetail.module.css';

// Helper function to convert Slate nodes to HTML
const slateToHtml = (nodes) => {
    if (!nodes || !Array.isArray(nodes)) {
        return '';
    }
    
    let html = '';
    
    nodes.forEach(node => {
        if (node.type === 'paragraph') {
            html += '<p>';
            node.children.forEach(child => {
                let text = child.text || '';
                if (child.bold) text = `<strong>${text}</strong>`;
                if (child.italic) text = `<em>${text}</em>`;
                if (child.underline) text = `<u>${text}</u>`;
                if (child.strikethrough) text = `<s>${text}</s>`;
                html += text;
            });
            html += '</p>';
        } else if (node.type === 'bulleted-list') {
            html += '<ul>';
            node.children.forEach(listItem => {
                html += '<li>';
                listItem.children.forEach(child => {
                    let text = child.text || '';
                    if (child.bold) text = `<strong>${text}</strong>`;
                    if (child.italic) text = `<em>${text}</em>`;
                    if (child.underline) text = `<u>${text}</u>`;
                    if (child.strikethrough) text = `<s>${text}</s>`;
                    html += text;
                });
                html += '</li>';
            });
            html += '</ul>';
        }
    });
    
    return html;
};

// Helper function to check if a string is HTML
const isHtmlString = (str) => {
    return typeof str === 'string' && (
        str.includes('<p>') || 
        str.includes('<ul>') || 
        str.includes('<li>') || 
        str.includes('<strong>') || 
        str.includes('<em>') || 
        str.includes('<u>') || 
        str.includes('<s>')
    );
};

// Helper function to truncate HTML safely
const truncateHtml = (html, maxLength) => {
    if (html.length <= maxLength) return html;
    
    // Create a temporary div to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Get the text content
    const text = tempDiv.textContent || tempDiv.innerText || '';
    
    if (text.length <= maxLength) return html;
    
    return html.substring(0, maxLength) + '...';
};

export const seeMoreTitle = (description, maxLength, setShowFullDescription, showFullDescription) => {
    const handelSeeMore = () => {
        setShowFullDescription(!showFullDescription);
    };

    // Check if content is in Slate format (array of objects)
    const isSlateFormat = Array.isArray(description) && description.length > 0 && typeof description[0] === 'object';
    
    // Process the description based on its format
    const processDescription = () => {
        if (!description) return '';
        
        // If it's Slate format, convert to HTML
        if (isSlateFormat) {
            return slateToHtml(description);
        }
        
        // If it's already HTML string
        if (isHtmlString(description)) {
            if (!showFullDescription) {
                return truncateHtml(description, maxLength);
            }
            return description;
        }
        
        // If it's plain text, process as before
        const normalizedText = String(description).replace(/\r\n/g, '\n');
        
        if (!showFullDescription) {
            const truncateAt = Math.min(maxLength, normalizedText.length);
            const truncated = normalizedText.substring(0, truncateAt);
            
            const lastNewLine = truncated.lastIndexOf('\n');
            if (lastNewLine > 0 && lastNewLine > truncateAt - 20) {
                return truncated.substring(0, lastNewLine) + '...';
            }
            return truncated + '...';
        }
        
        return normalizedText;
    };

    const content = processDescription();
    const shouldRenderAsHtml = isSlateFormat || isHtmlString(description);

    return (
        <div className={styles.seeMoreContainer}>
            {shouldRenderAsHtml ? (
                <div className={styles.richTextContent}>
                    <div 
                        dangerouslySetInnerHTML={{ __html: content }} 
                        className={styles.formattedDescription}
                    />
                    <span className={styles.seeMoreText} onClick={handelSeeMore}>
                        {!showFullDescription ? t("jobRequirements.seeMore") : t("jobRequirements.seeLess")}
                    </span>
                </div>
            ) : (
                <Typography
                    title={
                        <>
                            {content.split('\n').map((line, i, arr) => (
                                <span key={i}>
                                    {line}
                                    {i < arr.length - 1 && <br />}
                                </span>
                            ))}
                            <span className={styles.seeMoreText} onClick={handelSeeMore}>
                                {!showFullDescription ? t("jobRequirements.seeMore") : t("jobRequirements.seeLess")}
                            </span>
                        </>
                    }
                    weight={"400"}
                    fontSize={14}
                    style={{ whiteSpace: 'pre-line' }}
                />
            )}
        </div>
    );
};